import {
  COVERAGE_STATES,
  COVERAGE_STROKE_CLASS,
  type CoverageCounts,
  coverageTotal,
  describeCoverage,
} from "./coverage";

export interface CoverageRingProps {
  coverage: CoverageCounts;
  /** Diameter in px. */
  size?: number;
  /**
   * Expose the ring to assistive tech. Off by default because the ring
   * usually sits beside a visible step count; its title still shows on hover.
   */
  labelled?: boolean;
}

/** A small donut of a step-coverage distribution. */
export function CoverageRing({
  coverage,
  size = 16,
  labelled = false,
}: CoverageRingProps) {
  const total = coverageTotal(coverage);
  const strokeWidth = Math.max(2, size * 0.2);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0 -rotate-90"
      fill="none"
      strokeWidth={strokeWidth}
      role="img"
      aria-hidden={labelled ? undefined : true}
    >
      <title>{describeCoverage(coverage)}</title>
      {total === 0 ? (
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className={COVERAGE_STROKE_CLASS.none}
        />
      ) : (
        COVERAGE_STATES.filter((state) => coverage[state] > 0).map((state) => {
          const length = (coverage[state] / total) * circumference;
          const arc = (
            <circle
              key={state}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              className={COVERAGE_STROKE_CLASS[state]}
              strokeDasharray={`${length} ${circumference - length}`}
              strokeDashoffset={-offset}
            />
          );
          offset += length;
          return arc;
        })
      )}
    </svg>
  );
}
